import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import type { CloudApiClient, CloudPrincipal } from './api-client';
import { readCloudConfig } from './config';
import type { CloudConfigResult } from './config';
import { createCloudSessionManager } from './session';
import type { CloudSessionManager, CloudSessionSnapshot } from './session';

type CloudSessionContextValue = Readonly<{
  api: CloudApiClient | null;
  beginLogin: () => Promise<void>;
  config: CloudConfigResult;
  error: Error | null;
  isBusy: boolean;
  isOffline: boolean;
  logout: () => Promise<void>;
  principal: CloudPrincipal | null;
  retry: () => Promise<void>;
  status:
    | 'authenticated'
    | 'configuration-error'
    | 'disabled'
    | 'error'
    | 'restoring'
    | 'signed-out';
}>;

type CloudSessionProviderProps = Readonly<{
  callbackPath?: string;
  children: ReactNode;
  configResult?: CloudConfigResult;
  createManager?: typeof createCloudSessionManager;
  navigate?: (url: string) => void;
}>;

const CloudSessionContext = createContext<CloudSessionContextValue | null>(
  null,
);

export function CloudSessionProvider({
  callbackPath = '/cloud',
  children,
  configResult,
  createManager = createCloudSessionManager,
  navigate = url => window.location.assign(url),
}: CloudSessionProviderProps) {
  const [config] = useState(() => configResult ?? readCloudConfig());
  const [manager] = useState<CloudSessionManager | null>(() =>
    config.status === 'ready' ? createManager(config.config) : null,
  );
  const [snapshot, setSnapshot] = useState<CloudSessionSnapshot | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isBusy, setIsBusy] = useState(config.status === 'ready');

  async function restore(): Promise<void> {
    if (manager === null) return;
    setIsBusy(true);
    setError(null);
    try {
      const restored = await manager.restore();
      setSnapshot(restored);
      if (window.location.pathname.endsWith('/cloud/callback')) {
        window.history.replaceState({}, '', resolveCallbackPath(callbackPath));
      }
    } catch (restoreError) {
      setError(toError(restoreError));
    } finally {
      setIsBusy(false);
    }
  }

  useEffect(() => {
    if (manager === null) return;
    let isCurrent = true;
    setIsBusy(true);
    setError(null);
    void manager
      .restore()
      .then(restored => {
        if (!isCurrent) return;
        setSnapshot(restored);
        if (window.location.pathname.endsWith('/cloud/callback')) {
          window.history.replaceState(
            {},
            '',
            resolveCallbackPath(callbackPath),
          );
        }
      })
      .catch(restoreError => {
        if (isCurrent) setError(toError(restoreError));
      })
      .finally(() => {
        if (isCurrent) setIsBusy(false);
      });
    return () => {
      isCurrent = false;
    };
  }, [callbackPath, manager]);

  async function beginLogin(): Promise<void> {
    if (manager === null) return;
    setIsBusy(true);
    setError(null);
    try {
      navigate(await manager.beginLogin('/cloud'));
    } catch (loginError) {
      setError(toError(loginError));
      setIsBusy(false);
    }
  }

  async function logout(): Promise<void> {
    if (manager === null) return;
    setIsBusy(true);
    setError(null);
    const hostedLogoutUrl = await manager.completeLogout();
    setSnapshot({ status: 'signed-out' });
    setIsBusy(false);
    if (hostedLogoutUrl !== null) navigate(hostedLogoutUrl);
  }

  const isAuthenticated = snapshot?.status === 'authenticated';
  const status = resolveStatus(config, snapshot, error);
  const value: CloudSessionContextValue = {
    api: isAuthenticated ? (manager?.api ?? null) : null,
    beginLogin,
    config,
    error,
    isBusy,
    isOffline: isAuthenticated ? snapshot.isOffline : false,
    logout,
    principal: isAuthenticated ? snapshot.principal : null,
    retry: restore,
    status,
  };

  return (
    <CloudSessionContext.Provider value={value}>
      {children}
    </CloudSessionContext.Provider>
  );
}

export function useCloudSession(): CloudSessionContextValue {
  const context = useContext(CloudSessionContext);
  if (context === null) {
    throw new Error('useCloudSession must be used within CloudSessionProvider');
  }
  return context;
}

function resolveStatus(
  config: CloudConfigResult,
  snapshot: CloudSessionSnapshot | null,
  error: Error | null,
): CloudSessionContextValue['status'] {
  if (config.status === 'disabled') return 'disabled';
  if (config.status === 'invalid') return 'configuration-error';
  if (error !== null) return 'error';
  if (snapshot === null) return 'restoring';
  return snapshot.status;
}

function toError(value: unknown): Error {
  return value instanceof Error ? value : new Error('Unexpected cloud error.');
}

function resolveCallbackPath(callbackPath: string): string {
  const callbackRoute = '/cloud/callback';
  const pathname = window.location.pathname;
  const appBasePath = pathname.endsWith(callbackRoute)
    ? pathname.slice(0, -callbackRoute.length)
    : '';
  return `${appBasePath}${callbackPath}`;
}
